/**
 * Galeria: as seeds 0..N de um template, lado a lado.
 *
 * É a ferramenta que você vai usar o tempo todo. Ler o código de um gerador não conta
 * quantas das suas variantes ficaram FEIAS — uma barra que estoura, um rótulo que
 * colide, uma faixa de parâmetro que produz números absurdos. A grade conta em dois
 * segundos, e mostra também as seeds rejeitadas e por quê.
 *
 * E é também a vitrine para ESCOLHER variantes à mão: com `abrir`, cada variante
 * aceita vira um botão que abre a questão inteira — de onde ela pode ir para o
 * percurso com a seed fixada.
 */

import { generate } from '../engine/generate.ts';
import type { AnyTemplate } from '../engine/registry.ts';
import { seedSequence, type Seed } from '../core/rng.ts';
import { renderScene } from '../render/svg.ts';
import { toPlainText } from '../schema/richtext.ts';

export function renderGallery(
  template: AnyTemplate,
  count = 60,
  abrir?: (seed: Seed) => void,
): HTMLElement {
  const root = document.createElement('div');

  const resumo = document.createElement('p');
  resumo.className = 'notice';
  root.appendChild(resumo);

  const grade = document.createElement('div');
  grade.className = 'gallery';
  root.appendChild(grade);

  let aceitas = 0;
  const motivos: Record<string, number> = {};

  for (const s of seedSequence('s', 0, count)) {
    const r = generate(template, s);
    const fig = document.createElement('figure');
    const cap = document.createElement('figcaption');

    if (r.status === 'rejected') {
      motivos[r.reason] = (motivos[r.reason] ?? 0) + 1;
      cap.className = 'bad';
      cap.textContent = `${s} · ${r.reason}`;
      fig.appendChild(cap);
      grade.appendChild(fig);
      continue;
    }

    aceitas++;
    const inst = r.instance;
    const primeiro = inst.stimuli[0];

    if (primeiro?.kind === 'static-figure' || primeiro?.kind === 'measurable') {
      fig.appendChild(renderScene(primeiro.scene));
    } else if (primeiro?.kind === 'animated') {
      fig.appendChild(renderScene(primeiro.frame(primeiro.staticFallbackAt)));
    } else if (primeiro?.kind === 'manipulable') {
      const iniciais = Object.fromEntries(primeiro.controls.map((c) => [c.id, c.initial]));
      fig.appendChild(renderScene(primeiro.frame(iniciais)));
    } else {
      const p = document.createElement('p');
      p.textContent = toPlainText(inst.stem).slice(0, 120);
      fig.appendChild(p);
    }

    const chave =
      inst.interaction.kind === 'select-one' ? ` · chave ${inst.interaction.keyId}` : '';
    cap.textContent = `${s}${chave} · b̂ ${inst.difficulty.provisionalB.toFixed(2)}`;
    fig.appendChild(cap);

    if (abrir) {
      fig.classList.add('is-clicavel');
      fig.tabIndex = 0;
      fig.setAttribute('role', 'button');
      fig.setAttribute('aria-label', `Abrir a variante ${s}`);
      fig.title = `Abrir a variante ${s}`;
      fig.addEventListener('click', () => abrir(s));
      fig.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          abrir(s);
        }
      });
    }
    grade.appendChild(fig);
  }

  const detalhe = Object.entries(motivos)
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
  const taxa = ((count - aceitas) / count) * 100;
  resumo.textContent =
    `${aceitas} de ${count} seeds aceitas — taxa de rejeição ${taxa.toFixed(1)}%` +
    (detalhe ? ` (${detalhe})` : '') +
    (taxa > 20 ? ' ⚠ acima do orçamento de 20%: o espaço de parâmetros precisa ser revisto.' : '') +
    (abrir ? ' Clique numa variante para abri-la.' : '');

  return root;
}
